import { BrregCompanyData } from '../../../companies/hooks/useBrregCompanySearch';
import { OnboardingActions } from '../onboarding-actions';

interface StepSelskapProps {
  company: BrregCompanyData | null;
  isLoading?: boolean;
  onNext: () => void;
}

export function StepSelskap({ company, isLoading, onNext }: StepSelskapProps) {
  const formatAddress = (c: BrregCompanyData) => {
    const parts = [
      c.forretningsadresse?.adresse?.join(', '),
      c.forretningsadresse?.postnummer,
      c.forretningsadresse?.poststed,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  };

  if (!company) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Bekreft selskapet ditt</h2>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-brand-primary rounded-full animate-spin border-r-transparent mb-3" />
            <p className="text-white/50 text-sm">Henter selskapsdata...</p>
          </div>
        ) : (
          <p className="text-red-400 mt-4">
            Ingen selskapsdata funnet fra invitasjonen. Kontakt administrator.
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Bekreft selskapet ditt</h2>
      <p className="text-white/60 mb-8">
        Selskapet nedenfor er knyttet til din invitasjon og vil bli opprettet som en kundebase
      </p>

      <div className="w-full text-left p-5 rounded-lg border border-brand-primary bg-surface-selected">
        <div className="font-bold text-white text-lg">{company.navn}</div>
        <div className="text-white/50 text-sm mt-1">
          Org.nr. {company.organisasjonsnummer}
          {company.organisasjonsform && <> · {company.organisasjonsform.kode}</>}
          {company.registreringsdatoEnhetsregisteret && (
            <> · Stiftet {formatDate(company.registreringsdatoEnhetsregisteret)}</>
          )}
        </div>

        <div className="mt-4 grid grid-cols-[160px_1fr] gap-y-2 text-sm">
          {company.forretningsadresse?.adresse && (
            <>
              <span className="text-white/40">Adresse</span>
              <span className="text-white font-medium">{company.forretningsadresse.adresse.join(', ')}</span>
            </>
          )}
          {company.forretningsadresse?.postnummer && (
            <>
              <span className="text-white/40">Postnummer</span>
              <span className="text-white font-medium">{company.forretningsadresse.postnummer}</span>
            </>
          )}
          {company.forretningsadresse?.poststed && (
            <>
              <span className="text-white/40">Poststed</span>
              <span className="text-white font-medium">{company.forretningsadresse.poststed}</span>
            </>
          )}
          {company.forretningsadresse?.kommune && (
            <>
              <span className="text-white/40">Kommune</span>
              <span className="text-white font-medium">{company.forretningsadresse.kommune}</span>
            </>
          )}
          {company.organisasjonsform && (
            <>
              <span className="text-white/40">Organisasjonsform</span>
              <span className="text-white font-medium">
                {company.organisasjonsform.kode} — {company.organisasjonsform.beskrivelse}
              </span>
            </>
          )}
          {company.naeringskode1 && (
            <>
              <span className="text-white/40">Næringskode</span>
              <span className="text-white font-medium">
                {company.naeringskode1.kode} — {company.naeringskode1.beskrivelse}
              </span>
            </>
          )}
          {company.institusjonellSektorkode && (
            <>
              <span className="text-white/40">Sektorkode</span>
              <span className="text-white font-medium">
                {company.institusjonellSektorkode.kode} — {company.institusjonellSektorkode.beskrivelse}
              </span>
            </>
          )}
          {company.antallAnsatte != null && (
            <>
              <span className="text-white/40">Ansatte</span>
              <span className="text-white font-medium">{company.antallAnsatte}</span>
            </>
          )}
        </div>
      </div>

      <OnboardingActions
        showBack={false}
        onNext={onNext}
        nextDisabled={false}
      />
    </div>
  );
}
